import { useState, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  initVideoUpload,
  confirmVideoUpload,
  uploadToPresignedUrl,
  submitExternalLink,
  getVideoStatus,
  deleteVideo,
  type VideoStatusResponse,
  type ExternalLinkRequest,
} from '@/services/introVideoService';

export type UploadState = 'idle' | 'uploading' | 'processing' | 'ready' | 'failed' | 'error';

interface UseVideoUploadOptions {
  accessToken: string;
}

export function useVideoUpload({ accessToken }: UseVideoUploadOptions) {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const queryClient = useQueryClient();

  // Poll video status (enabled when processing or on mount to detect existing state)
  const { data: videoStatus, refetch } = useQuery<VideoStatusResponse>({
    queryKey: ['videoStatus'],
    queryFn: () => getVideoStatus(accessToken),
    enabled: !!accessToken,
    refetchInterval: uploadState === 'processing' ? 5000 : false,
    refetchOnWindowFocus: false,
  });

  // Sync local state with server state
  if (videoStatus && uploadState !== 'uploading') {
    const serverState = videoStatus.status;
    if (serverState === 'READY' && uploadState !== 'ready') {
      setUploadState('ready');
    } else if (serverState === 'PROCESSING' && uploadState !== 'processing') {
      setUploadState('processing');
    } else if (serverState === 'FAILED' && uploadState !== 'failed') {
      setUploadState('failed');
      setError(videoStatus.failureReason || 'Processing failed');
    } else if (serverState === 'NONE' && uploadState !== 'idle' && uploadState !== 'uploading') {
      setUploadState('idle');
    }
  }

  const upload = useCallback(async (file: File) => {
    setError(null);
    setProgress(0);
    setUploadState('uploading');

    try {
      // Step 1: Init upload
      const initResponse = await initVideoUpload(
        {
          fileName: file.name,
          contentType: file.type,
          fileSizeBytes: file.size,
        },
        accessToken
      );

      // Step 2: Upload to S3 via presigned URL
      await uploadToPresignedUrl(initResponse.presignedUrl, file, setProgress);

      // Step 3: Confirm upload
      await confirmVideoUpload(initResponse.videoUploadId, accessToken);

      setUploadState('processing');
      setProgress(100);

      // Start polling
      queryClient.invalidateQueries({ queryKey: ['videoStatus'] });
    } catch (err) {
      setUploadState('error');
      setError(err instanceof Error ? err.message : 'Upload failed');
    }
  }, [accessToken, queryClient]);

  const submitLink = useCallback(async (request: ExternalLinkRequest) => {
    setError(null);
    try {
      await submitExternalLink(request, accessToken);
      setUploadState('ready');
      queryClient.invalidateQueries({ queryKey: ['videoStatus'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit link');
    }
  }, [accessToken, queryClient]);

  const remove = useCallback(async () => {
    setError(null);
    try {
      await deleteVideo(accessToken);
      setUploadState('idle');
      setProgress(0);
      queryClient.invalidateQueries({ queryKey: ['videoStatus'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete video');
    }
  }, [accessToken, queryClient]);

  const retry = useCallback(() => {
    setError(null);
    setUploadState('idle');
    setProgress(0);
  }, []);

  const cancel = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort();
    }
    setUploadState('idle');
    setProgress(0);
  }, []);

  return {
    uploadState,
    progress,
    error,
    videoStatus,
    upload,
    submitLink,
    remove,
    retry,
    cancel,
    refetch,
  };
}
